import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  FaGithub,
  FaGlobe,
  FaUsers,
  FaLightbulb,
  FaLayerGroup,
  FaCogs,
  FaCheckCircle,
  FaBolt,
} from "react-icons/fa";
import projects from "../data/projects";
import "../styles/ProjectDetail.css";

const featureIcons = [FaLightbulb, FaBolt, FaCogs, FaCheckCircle];

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  const gallery = project?.gallery || [];
  const features = project?.features || [];
  const stack = project?.stack || { frontend: [], backend: [], database: [], tools: [] };
  const architectureFlow = project?.architectureFlow || [];
  const challengesSolutions = project?.challengesSolutions || [];
  const impact = project?.impact || [];

  if (!project) {
    return (
      <section className="project-detail not-found">
        <h2>Project not found</h2>
        <p>The project you're looking for doesn't exist.</p>
        <Link to="/">Back to home</Link>
      </section>
    );
  }

  return (
    <section className="project-detail">
      <div className="case-container">
        <div className="case-hero">
          <div className="hero-copy">
            <span className="eyebrow">Case Study</span>
            <h1>{project.title}</h1>
            <p className="hero-tagline">{project.tagline}</p>
            <div className="hero-actions">
              <Link className="btn primary" to="/">
                Back to Projects
              </Link>
              {project.repo ? (
                <a className="btn ghost" href={project.repo} target="_blank" rel="noreferrer">
                  <FaGithub />
                  GitHub
                </a>
              ) : null}
              {project.live ? (
                <a className="btn ghost" href={project.live} target="_blank" rel="noreferrer">
                  <FaGlobe />
                  Live Demo
                </a>
              ) : null}
            </div>
            <div className="hero-meta">
              <span>{project.role}</span>
              <span>{project.status}</span>
              <span>{project.type}</span>
            </div>
          </div>

          <div className="hero-media">
            <img className="main-mock" src={project.image} alt={project.title} />
            {gallery[0] ? <img className="float one" src={gallery[0]} alt="App screen" /> : null}
            {gallery[1] ? <img className="float two" src={gallery[1]} alt="App screen" /> : null}
            <div className="hero-glow" />
          </div>
        </div>

        <section className="case-section overview">
          <div>
            <h2>Overview</h2>
            <p className="section-lead">App for sales representatives to manage orders and customer interactions.</p>
          </div>
          <div className="overview-grid">
            <div>
              <h3>Problem</h3>
              <p>{project.overview?.problem || ""}</p>
            </div>
            <div>
              <h3>Solution</h3>
              <p>{project.overview?.solution || ""}</p>
            </div>
            <div>
              <h3>Target Users</h3>
              <p>{project.overview?.users || ""}</p>
            </div>
          </div>
        </section>

        <section className="case-section features">
          <div>
            <h2>Key Features</h2>
            <p className="section-lead">Core capabilities that make the product valuable.</p>
          </div>
          <div className="feature-grid">
            {features.map((feature, idx) => {
              const Icon = featureIcons[idx % featureIcons.length];
              return (
                <div key={feature} className="feature-card">
                  <Icon />
                  <p>{feature}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="case-section tech">
          <div>
            <h2>Tech Stack</h2>
            <p className="section-lead">A categorized stack built for scale and speed.</p>
          </div>
          <div className="tech-grid">
            <div>
              <h3>Frontend</h3>
              <div className="badge-row">
                {stack.frontend.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            </div>
            <div>
              <h3>Backend</h3>
              <div className="badge-row">
                {stack.backend.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            </div>
            <div>
              <h3>Database</h3>
              <div className="badge-row">
                {stack.database.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            </div>
            <div>
              <h3>Tools</h3>
              <div className="badge-row">
                {stack.tools.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="case-section architecture">
          <div>
            <h2>Architecture</h2>
           
          </div>
          <div className="architecture-flow">
              {project.architectureImage ? (
                <div className="architecture-image-wrap">
                  <img
                    className="architecture-image"
                    src={project.architectureImage}
                    alt="Architecture diagram"
                  />
                </div>
              ) : null}
              {architectureFlow.map((step) => (
                <div key={step} className="flow-step">
                  <FaLayerGroup />
                  <span>{step}</span>
                </div>
              ))}
          </div>
        </section>

        <section className="case-section challenges">
          <div>
            <h2>Challenges & Solutions</h2>
            <p className="section-lead">Real problems solved through engineering decisions.</p>
          </div>
          <div className="challenge-grid">
            {challengesSolutions.map((item) => (
              <div key={item.challenge} className="challenge-card">
                <h4>{item.challenge}</h4>
                <p>{item.solution}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="case-section gallery">
          <div>
            <h2>Demo Gallery</h2>
            <p className="section-lead">Scroll to explore key screens and flows.</p>
          </div>
          <div className="gallery-strip">
            {gallery.map((img, idx) => (
              <img key={`${project.slug}-${idx}`} src={img} alt={`${project.title} screen ${idx + 1}`} />
            ))}
          </div>
        </section>

        <section className="case-section impact">
          <div>
            <h2>Results & Impact</h2>
            <p className="section-lead">Outcome-driven highlights for stakeholders.</p>
          </div>
          <div className="impact-grid">
            {impact.map((item) => (
              <div key={item} className="impact-card">
                <FaUsers />
                <p>{item}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </section>
  );
}
